import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import {
  fnGetWhLocCustLotCtPltInv,
  fnGetWhSnInv,
} from "@shared/utils/InvUtils";
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, ExecuteRunner } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnLabel from "@shared/components/SnLabel";
import SnDateBox from "@shared/components/SnDateBox";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";
import SnNumberBox from "@shared/components/SnNumberBox";

interface Props {
  Menuprops: menuInfo;
}

interface TabState {
  page: string;
  searchParams: any | null;
}

const SN_MTL_MOW_REG_008 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
    grid5: React.createRef<SnGridRef>(),
    grid6: React.createRef<SnGridRef>(),
    grid7: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const txtBarcodeRef = useRef<any>(null);
  const numberBoxFifoRef = useRef<any>(null);

  const isFirstRender = useRef(true); // 화면 오픈시 조회 방지

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  //#endregion

  // 선택 재고리스트
  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);

  // 스위치
  const switchRef = useRef<SnSwitchHandle>(null);

  // 처리 컴포넌트
  const [dateBox, setDateBoxValue] = useState<string>(dateToString(new Date()));

  const [tabPageState, setTabState] = useState<TabState>({
    page: "1",
    searchParams: null,
  });

  const dateValueChanged = async (value: string) => {
    setDateBoxValue(value);
  };

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
    grid6: [],
    grid7: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid5: [],
    grid6: [],
    grid7: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
          grid5: rtn.grid.grid5,
          grid6: rtn.grid.grid6,
          grid7: rtn.grid.grid7,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (tabPageState.page) {
      if (!tabPageState.searchParams) {
        onSearch(null);
      } else {
        onSearch(tabPageState.searchParams);
      }
    }
  }, [tabPageState.page]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onExecute,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      if (tabPageState.page === "1") {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_mow_reg_008_01",
          params: searchDataToUse,
        };

        setLotSnData([]);

        getGridCurrent("grid1").resetUpdate();
        getGridCurrent("grid2").resetUpdate();
        getGridCurrent("grid3").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: data[0],
          grid2: data[1] || [],
          grid3: data[2] || [],
          grid4: [],
          grid5: [],
          grid6: [],
          grid7: [],
        });
      } else {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_mow_reg_008_02",
          params: searchDataToUse,
        };

        getGridCurrent("grid5").resetUpdate();
        getGridCurrent("grid6").resetUpdate();
        getGridCurrent("grid7").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: [],
          grid2: [],
          grid3: [],
          grid4: [],
          grid5: data[0],
          grid6: data[1] || [],
          grid7: data[2] || [],
        });
      }
    } catch (error: any) {}
  };
  //#endregion

  //#region onExecute
  const onExecute = async (id: string) => {
    let hasInvalid = false;

    try {
      switch (id) {
        case "Save": // 처리
          {
            const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            if (dateBox === "") {
              await showDialog("alter", "출하일을 입력해 주세요.");
              return false;
            }

            const confirm = await showDialog("confirm", "처리하시겠습니까?");

            if (!confirm) {
              return; // 사용자가 아니오를 선택하면 함수 종료
            }

            let hasInvalidcdItem = "";

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              if (node.data.qt > node.data.qt_req_rev) {
                if (!hasInvalid) {
                  hasInvalid = true;
                  hasInvalidcdItem = node.data.cd_item;
                }
              }
            });

            if (hasInvalid) {
              const confirm = await showDialog(
                "confirm",
                "품목코드 [" +
                  hasInvalidcdItem +
                  "]의 요청수량 대비 인계수량이 부족합니다.\n계속 하시겠습니까?"
              );
              if (!confirm) return;
            }

            const isureqH: any[] = [];
            const isureqL: any[] = [];
            const reqLotSn: any[] = [];

            const updateDataH = {
              cd_firm: userInfo.cd_firm,
              cd_biz: focusedRowNodeH.data.cd_biz,
              no_req: focusedRowNodeH.data.no_req,
              st_req: "F",
            };
            isureqH.push(updateDataH);

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              const updateDataL = {
                cd_firm: userInfo.cd_firm,
                no_req: node.data.no_req,
                no_line: node.data.no_line,
                fg_unit: node.data.fg_unit,
                qt_req_rev: node.data.qt_req_rev,
                st_req: "F",
              };
              isureqL.push(updateDataL);

              if (node.data.fg_unit === "QT") {
                if (node.data.qt_req_rev !== 0) {
                  const qt = {
                    cd_firm: userInfo.cd_firm,
                    no_req: node.data.no_req,
                    no_line: node.data.no_line,
                    cd_item: node.data.cd_item,
                    qt_good: node.data.qt_good,
                    qt_bad: node.data.qt_bad,
                    qt: node.data.qt,
                    fg_unit: node.data.fg_unit,
                  };
                  reqLotSn.push(qt);
                }
              }
            });

            getGridCurrent("grid4").forEachNode((node: RowNode) => {
              if (node.data.no_req == focusedRowNodeH.data.no_req) {
                const lotSn = {
                  cd_firm: userInfo.cd_firm,
                  cd_biz: focusedRowNodeH.data.cd_biz,
                  no_req: node.data.no_req,
                  no_line: node.data.no_line,
                  no_barcode: node.data.no_barcode,
                  cd_item: node.data.cd_item,
                  cd_wh: node.data.cd_wh,
                  qt: node.data.qt_good,
                  qt_good: node.data.qt_good,
                  no_io: node.data.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  no_line_io: node.data.no_line_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  fg_unit: node.data.fg_unit,
                  no_ct: node.data.no_ct,
                  no_plt: node.data.no_plt,
                  dt_io_origin: node.data.dt_io_origin, // 원 LOT 입고일자
                  no_lot_cust: node.data.no_lot_cust, // 원 고객사 LOT
                };
                reqLotSn.push(lotSn);
              }
            });

            // 요청 데이터 구성
            const SaveData = {
              isureqh: { update: isureqH },
              isureql: { update: isureqL },
              req_lotsn: { insert: reqLotSn },
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_MOW_REG_008_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid1").resetUpdate();
            getGridCurrent("grid2").resetUpdate();
            getGridCurrent("grid3").resetUpdate();
            setLotSnData([]);

            onSearch(null);
          }
          break;
        case "Delete": // 취소
          {
            const focusedRowNodeH = getGridCurrent("grid5").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            getGridCurrent("grid6").forEachFilteredNode((node: RowNode) => {
              if (node.data.yn_req === "N") {
                if (!hasInvalid) {
                  hasInvalid = true;
                }
              }
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "해당 메뉴에서 취소할 수 없는 내역입니다. [자재불출인계등록] 메뉴에서 취소하시기 바랍니다."
              );
              return false;
            }

            getGridCurrent("grid6").forEachFilteredNode((node: RowNode) => {
              if (node.data.qt_io > 0) {
                if (!hasInvalid) {
                  hasInvalid = true;
                }
              }
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "인수 처리된 내역입니다. 인수내역 삭제 후 진행해 주시기 바랍니다."
              );
              return false;
            }

            const confirm = await showDialog("confirm", "취소하시겠습니까?");

            if (!confirm) {
              return; // 사용자가 아니오를 선택하면 함수 종료
            }

            const isureqH: any[] = [];
            const isureqL: any[] = [];
            const reqLotSn: any[] = [];

            const updateDataH = {
              cd_firm: userInfo.cd_firm,
              cd_biz: focusedRowNodeH.data.cd_biz,
              no_req: focusedRowNodeH.data.no_req,
              st_req: "P",
            };
            isureqH.push(updateDataH);

            getGridCurrent("grid6").forEachFilteredNode((node: RowNode) => {
              const updateDataL = {
                cd_firm: userInfo.cd_firm,
                no_req: node.data.no_req,
                no_line: node.data.no_line,
                fg_unit: node.data.fg_unit,
                qt_req_rev: 0,
                st_req: "P",
              };
              isureqL.push(updateDataL);
            });

            getGridCurrent("grid7").forEachNode((node: RowNode) => {
              if (node.data.no_req == focusedRowNodeH.data.no_req) {
                const lotSn = {
                  cd_firm: userInfo.cd_firm,
                  cd_biz: focusedRowNodeH.data.cd_biz,
                  no_req: node.data.no_req,
                  no_line: node.data.no_line,
                  no_barcode: node.data.no_barcode,
                  cd_item: node.data.cd_item,
                  cd_wh: node.data.cd_wh,
                  qt: node.data.qt_good,
                  qt_good: node.data.qt_good,
                  no_io: node.data.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  no_line_io: node.data.no_line_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  fg_unit: node.data.fg_unit,
                  no_ct: node.data.no_ct,
                  no_plt: node.data.no_plt,
                };
                reqLotSn.push(lotSn);
              }
            });

            // 요청 데이터 구성
            const SaveData = {
              isureqh: { update: isureqH },
              isureql: { update: isureqL },
              req_lotsn: { delete: reqLotSn },
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_MOW_REG_008_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid5").resetUpdate();
            getGridCurrent("grid6").resetUpdate();
            getGridCurrent("grid7").resetUpdate();

            setTabState({ page: "1", searchParams: null });
          }
          break;
      }
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      getGridCurrent("grid5").resetUpdate(true);
      getGridCurrent("grid6").resetUpdate(true);
      getGridCurrent("grid7").resetUpdate(true);
    }
  };
  //#endregion

  //#region GridL Events
  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "qt_good":
      case "qt_bad":
        rowNode.setDataValue(
          "qt_req_rev",
          rowData.qt_good + rowData.qt_bad,
          "noEvent"
        );
        break;
      case "cd_wh":
      case "nm_wh":
        if (
          rowData.cd_wh !== "" &&
          rowData.cd_wh !== null &&
          rowData.cd_wh !== undefined
        ) {
          if (rowData.fg_unit !== "QT") {
            rowData.qt_req_rev = 0;
          }
          resetRowData();

          const data = {
            cd_biz: rowData.cd_biz,
            cd_item: rowData.cd_item,
            cd_wh: rowData.cd_wh,
            cd_loc: "",
          };

          if (
            !lotSnDataRef.current.some(
              (item) =>
                item.cd_item === rowData.cd_item && item.cd_wh === rowData.cd_wh
            )
          ) {
            switch (rowData.fg_unit) {
              case "LOT":
                const searchLotData = await fnGetWhLocCustLotCtPltInv(data);
                const lotData = searchLotData.map((item: any) => {
                  const { no_lot, ...rest } = item; // no_lot만 추출

                  return {
                    ...rest,
                    no_barcode: no_lot, // 필드명 바꾸기
                    fg_unit: "LOT", // LOT/SN구분
                    qt_isu: 0, // 적용량 추가
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setDataSources((prevData: any) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const dataSource = prevData.grid3 || [];

                  const isAlreadyExist = dataSource.some(
                    (item: any) =>
                      item.cd_item === rowData.cd_item &&
                      item.cd_wh === rowData.cd_wh
                  );
                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return {
                    ...prevData,
                    grid3: [...dataSource, ...lotData],
                  };
                });
                break;
              case "SN":
                const searchSnData = await fnGetWhSnInv(data);
                const snData = searchSnData.map((item: any) => {
                  const { no_sn, ...rest } = item;

                  return {
                    ...rest,
                    no_barcode: no_sn, // 필드명 바꾸기
                    fg_unit: "SN", // LOT/SN구분
                    no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    qt_isu: 1, // 적용량 추가
                    no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                    no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                    no_lot_cust: "", // undefined 방지
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setDataSources((prevData: any) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const dataSource = prevData.grid3 || [];

                  const isAlreadyExist = dataSource.some(
                    (item: any) =>
                      item.cd_item === rowData.cd_item &&
                      item.cd_wh === rowData.cd_wh
                  );
                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return {
                    ...prevData,
                    grid3: [...dataSource, ...snData],
                  };
                });
                break;
            }
          }
          // 재고리스트 필터돌게
          getGridCurrent("grid2").clearFocus();
          getGridCurrent("grid2").selectAndFocusRow(rowNode.rowIndex);
        } else {
          resetRowData();
        }
        break;
    }
  };

  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      getGridCurrent("grid3").applyFilters({
        cd_item: selectedRow.cd_item,
        cd_wh: selectedRow.cd_wh,
        qt_inv: "notZero",
      });

      const qtRemain = selectedRow.qt - selectedRow.qt_req_rev;

      if (qtRemain > 0 && selectedRow.fg_unit !== "QT") {
        numberBoxFifoRef.current.setValue(qtRemain);
      } else {
        numberBoxFifoRef.current.setValue(0);
      }
    }
  };
  //#endregion

  //#region onCellValueChangedLotSn
  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_isu") {
      if (rowData.qt_inv < rowData.qt_isu) {
        showDialog("alter", "재고량을 초과했습니다.");
        rowNode.setDataValue("qt_isu", 0);
      }
    }
  };

  const onRowDataUpdatedLotSn = () => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    // 신규일 때만 바코드 컴포넌트 포커스 타게
    if (focusedRowNodeL) {
      if (focusedRowNodeL.data.state === "insert") {
        txtBarcodeRef.current.focus();
      }
    }
  };
  //#endregion

  //#region GridD Events
  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        selectInv("C", "");
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
        const focusedNodeD = getGridCurrent("grid4").focusedRowNode();

        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (
            node.data.cd_item === focusedNodeD.data?.cd_item &&
            node.data.cd_wh === focusedNodeD.data?.cd_wh &&
            node.data.no_barcode === focusedNodeD.data?.no_barcode &&
            node.data.dt_io === focusedNodeD.data?.dt_io_origin &&
            node.data.no_lot_cust === focusedNodeD.data?.no_lot_cust
          ) {
            node.setDataValue(
              "qt_inv",
              node.data.qt_inv + focusedNodeD.data?.qt_good
            );

            if (node.data.fg_unit === "SN") {
              node.setDataValue("qt_isu", 1);
            }

            focusedRowNodeL.setDataValue(
              "qt_req_rev",
              focusedRowNodeL.data?.qt_req_rev - focusedNodeD.data?.qt_good
            );
            return;
          }
        });
        getGridCurrent("grid4").deleteSelectedRows();
        getGridCurrent("grid3").applyFilters({
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeL.data.cd_wh,
          qt_inv: "notZero",
        });
        break;
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeH.data.cd_wh,
      qt_inv: "notZero",
    });

    const barcode = e;

    if (barcode === "" || barcode === null || barcode === undefined) {
      return;
    }

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      selectInv("S", barcode);
    } else {
      getGridCurrent("grid3").applyLikeFilters(
        {
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeH.data.cd_wh,
          qt_inv: "notZero",
          no_barcode: barcode,
          no_ct: barcode,
          no_plt: barcode,
        },
        ["no_barcode", "no_ct", "no_plt"]
      );
    }
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    switch (id) {
      case "Fifo":
        selectInv("F", "");
    }
  };
  //#endregion

  //#region Method
  const selectInv = (id: string, barcode: string) => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    let selectedMap = new Map<string, number>(); // 재고선택 키/수량 Map
    let qtAdd = 0,
      qtRemain = 0;

    switch (id) {
      case "F": // 선입선출
        if (numberBoxFifoRef.current.getValue == 0) {
          showDialog("alter", "수량이 입력되지 않았습니다.");
          return;
        }
        qtRemain = Number(numberBoxFifoRef.current.getValue);
        break;
      case "C": // 재고선택
        getGridCurrent("grid3").stopEditing();
        const selectedLotSn = getGridCurrent("grid3").getCheckedRows();
        if (selectedLotSn.length < 1) {
          showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        }

        selectedLotSn.forEach((row) => {
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}_${row.no_lot_cust}`;
          // 사용자가 그리드3에 입력한 '출고예정수량(qt_isu)'을 가져옴
          selectedMap.set(key, Number(row.qt_isu) || 0);
        });
        break;
      case "S": // 스캔
        // 요청량 있으면 요청량이 잔량
        if (focusedRowNodeL.data.qt_req !== 0) {
          qtRemain = focusedRowNodeL.data.qt - focusedRowNodeL.data.qt_req_rev;

          if (qtRemain === 0) {
            showDialog("alter", "인계수량이 다 등록되었습니다.");
            return false;
          }
        }
        // 요청량이 없으면 바코드 전체 선택
        else {
          qtRemain = -1;
        }
        break;
    }

    getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;

      switch (id) {
        case "F": // 선입선출
          if (qtRemain <= 0) return;

          qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
          break;
        case "C": // 선택
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}_${row.no_lot_cust}`;
          if (!selectedMap.has(key)) return;

          qtIsu = selectedMap.get(key) || 0; // 선택량 가져옴
          break;
        case "S": // 스캔
          // qtRemain === -1은 요청량 없는 건임 바코드찍은만큼 전체 출고
          if (
            String(row.no_barcode ?? "").toLowerCase() ===
              barcode.toLowerCase() ||
            String(row.no_ct ?? "").toLowerCase() === barcode.toLowerCase() ||
            String(row.no_plt ?? "").toLowerCase() === barcode.toLowerCase()
          ) {
            if (qtRemain !== -1) {
              if (qtRemain <= 0) return;

              qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
            } else {
              qtIsu = row.qt_inv;
            }
          }
          break;
      }

      if (qtIsu <= 0) return;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeL.data?.cd_biz,
        no_req: focusedRowNodeL.data?.no_req,
        no_line: focusedRowNodeL.data?.no_line,
        cd_item: focusedRowNodeL.data?.cd_item,
        cd_wh: focusedRowNodeL.data?.cd_wh,
        no_barcode: node.data.no_barcode,
        fg_unit: node.data.fg_unit,
        qt_good: qtIsu,
        no_ct: node.data.no_ct,
        no_plt: node.data.no_plt,
        no_io: node.data.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_line_io: node.data.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        dt_io_origin: node.data.dt_io, // 원 LOT 입고일자
        no_lot_cust: node.data.no_lot_cust, // 원 고객사 LOT
      };

      // qtRemain === -1은 요청량 없는 건
      if (qtRemain !== -1) {
        qtRemain -= qtIsu; // 잔량 빼주고
      }
      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0);
      node.setDataValue("qt_inv", node.data.qt_inv - qtIsu);

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
        if (
          node4.data.cd_item === focusedRowNodeL.data?.cd_item &&
          node4.data.cd_wh === focusedRowNodeL.data?.cd_wh &&
          node4.data.no_barcode === node.data.no_barcode &&
          node4.data.dt_io_origin === node.data.dt_io &&
          node4.data.no_ct === node.data.no_ct &&
          node4.data.no_plt === node.data.no_plt &&
          node4.data.no_lot_cust === node.data.no_lot_cust
        ) {
          node4.setDataValue("qt_good", node4.data.qt_good + qtIsu);
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid4").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue(
      "qt_req_rev",
      focusedRowNodeL.data?.qt_req_rev + qtAdd
    );
    numberBoxFifoRef.current.setValue(0);

    getGridCurrent("grid3").clearCheckBox();
    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeL.data.cd_wh,
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };

  const resetRowData = (): any => {
    if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          node4.data.cd_item === node.data?.cd_item &&
          node4.data.cd_wh === node.data?.cd_wh &&
          node4.data.no_barcode === node.data?.no_barcode
        ) {
          node.setDataValue("qt_inv", node.data.qt_inv + node4.data.qt_good);

          if (node.data?.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }
          return;
        }
      });
    });
    getGridCurrent("grid4").deleteAllRows();

    getGridCurrent("grid3").applyFilters({
      cd_item: "",
      cd_wh: "",
    });
  };
  //#endregion

  //#region onTabChanged
  const onTabChanged = async (index: string) => {
    setTabState({ page: index, searchParams: null });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
          onFocusedRowChanged: onFocusedRowChangedL,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onCellValueChanged: onCellValueChangedLotSn,
          onRowDataUpdated: onRowDataUpdatedLotSn,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: lotSnData,
        events: {
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
      grid5: {
        columns: columns.grid5,
        dataSource: dataSources.grid5,
      },
      grid6: {
        columns: columns.grid6,
        dataSource: dataSources.grid6,
      },
      grid7: {
        columns: columns.grid7,
        dataSource: dataSources.grid7,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnTab
          titles={["미처리", "처리"]}
          width={"100%"}
          selectedTab={tabPageState.page}
          onTabChanged={onTabChanged}
          header={[
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <div style={{ width: 60 }}>
                    <SnLabel text="인계일" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnDateBox
                      defaultValue={dateBox}
                      onValueChanged={dateValueChanged}
                    />
                  </div>
                  <div style={{ paddingLeft: "80px" }}>
                    <SnButton
                      text={"처리"}
                      onClick={() => {
                        ExecuteRunner.run("Save");
                      }}
                    />
                  </div>
                </div>,
              ]}
            />,
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <SnButton
                    text={"취소"}
                    onClick={() => {
                      ExecuteRunner.run("Delete");
                    }}
                  />
                </div>,
              ]}
            />,
          ]}
          body={[
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
                <SnGrid
                  ref={gridRefs.current["grid1"]} // Grid Name
                  columnConfig={grids.grid1.columns} // 컬럼 설정 전달
                  rowData={grids.grid1.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  filter={{
                    refs: [gridRefs.current["grid2"]],
                    columns: ["no_req"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel2" orgSize="64.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
                    <SnGrid
                      ref={gridRefs.current["grid2"]} // Grid Name
                      columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                      rowData={grids.grid2.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                      events={grids.grid2.events}
                      filter={{
                        refs: [gridRefs.current["grid4"]],
                        columns: ["no_req", "no_line"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
                    <SnResizeBar direction={"height"} id="panel4" orgSize="50%">
                      <SnGrid
                        ref={gridRefs.current["grid3"]} // Grid Name
                        columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                        rowData={grids.grid3.dataSource} // Datasource
                        etcProps={{
                          rowSelection: "single",
                          title: "재고리스트",
                          ischeckboxSelection: true,
                        }}
                        events={grids.grid3.events}
                        header={
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <SnSwitch
                              ref={switchRef}
                              labelCheck="검색"
                              labelUncheck="스캔"
                              defaultChecked={false}
                            />
                            <div>
                              <SnBarcodeBox
                                onEnter={onBarcodeEnter}
                                inputRef={txtBarcodeRef}
                              />
                            </div>
                            <div className="w-[80px]">
                              <SnNumberBox
                                inputRef={numberBoxFifoRef}
                                customFormat="QT"
                              />
                            </div>
                            <div>
                              <SnButton
                                text="선입선출"
                                onClick={() => {
                                  onSnButtonClick("Fifo");
                                }}
                              />
                            </div>
                          </div>
                        }
                      />
                    </SnResizeBar>
                    <div className="flex-1 min-h-0">
                      <SnGrid
                        ref={gridRefs.current["grid4"]} // Grid Name
                        columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                        rowData={lotSnData} // Datasource
                        etcProps={{ rowSelection: "single", title: "자재정보" }}
                        events={grids.grid4.events}
                      />
                    </div>
                  </div>
                </div>
              </SnResizeBar>
            </div>,
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel5" orgSize="35.5%">
                <SnGrid
                  ref={gridRefs.current["grid5"]} // Grid Name
                  columnConfig={grids.grid5.columns} // 컬럼 설정 전달
                  rowData={grids.grid5.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  filter={{
                    refs: [gridRefs.current["grid6"]],
                    columns: ["no_req"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel6" orgSize="64.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnResizeBar direction={"width"} id="panel7" orgSize="60%">
                    <SnGrid
                      ref={gridRefs.current["grid6"]} // Grid Name
                      columnConfig={grids.grid6.columns} // 컬럼 설정 전달
                      rowData={grids.grid6.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                      filter={{
                        refs: [gridRefs.current["grid7"]],
                        columns: ["no_req", "no_line", "yn_filter"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
                    <SnGrid
                      ref={gridRefs.current["grid7"]} // Grid Name
                      columnConfig={grids.grid7.columns} // 컬럼 설정 전달
                      rowData={grids.grid7.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "자재정보" }}
                    />
                  </div>
                </div>
              </SnResizeBar>
            </div>,
          ]}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MOW_REG_008;
